import { Layout } from 'components/common/layout/Layout';
import { WritableDraft } from 'immer/dist/internal';
import { GetStaticProps } from 'next';
import { useContext, useEffect, useMemo, useState } from 'react';
import 'reflect-metadata';
import { container } from 'tsyringe';
import { SearchActionType, SearchBarContext, SearchBarContextType, createSearchBarProvider } from '../../client/hooks';
import { CustomSEO } from '../../components/common/layout/HeadMeta';
import { ArticleCard } from '../../components/news/ArticleCard';
import { PageListHeader } from '../../components/sections/PageListHeader';
import { ContentfulService } from '../../server/services/contentful.service';
import { PostSummary } from '../../types/shared.types';

export function useNewsSearchContext(): SearchBarContextType<PostSummary> {
    return useContext(SearchBarContext);
}
const NEWS_PER_PAGE = 9;
const BREAKPOINT = 1300;
function ArticleDisplay() {
    const { data, dispatch, hasMore } = useNewsSearchContext();
    const [windowWidth, setWindowWidth] = useState<number>(1500);

    const articles = useMemo(() => {
        if (!hasMore) {
            return data;
        }
        if (windowWidth <= BREAKPOINT) {
            return data.slice(0, data.length - (data.length % 2));
        }
        return data;
    }, [data, windowWidth, hasMore]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div className="container news-list-cards">
            {articles.length === 0 && <div>No articles found</div>}
            {articles.map((article: PostSummary, index: number) => (
                <ArticleCard key={`news-card-${index}`} article={article} />
            ))}
            <ShowMoreNewsSearchResultsButton hasMore={hasMore} dispatch={dispatch} />
        </div>
    );
}

export function ShowMoreNewsSearchResultsButton({
    hasMore,
    dispatch,
}: {
    hasMore: boolean;
    dispatch: (action: SearchActionType<any>) => void;
}) {
    if (!hasMore) {
        return <></>;
    }
    return (
        <button className="show-more button blue-button medium-button" onClick={() => dispatch({ type: 'loadMore' })}>
            SHOW MORE
        </button>
    );
}

function News_Index({ articles }: { articles: Array<PostSummary> }) {
    const SearchBarProvider = createSearchBarProvider(SearchBarContext);
    const newsReducer = function (
        draft: WritableDraft<{
            offset: number;
            searchData: PostSummary[];
            fullData: PostSummary[];
            hasMore: boolean;
            lastSearchText: string;
            lastSearchFiltersActive: boolean;
        }>,
        action: SearchActionType,
    ) {
        switch (action.type) {
            case 'searchData':
                if (action.value === '') {
                    return {
                        offset: 0,
                        hasSearched: false,
                        searchData: articles.slice(0, NEWS_PER_PAGE),
                        fullData: articles,
                        hasMore: articles.length > NEWS_PER_PAGE,
                        lastSearchText: '',
                        lastSearchFiltersActive: false,
                    };
                } else {
                    const re = new RegExp(`${action.value}`, 'i');
                    const found = articles.filter((article) => re.test(article.title) || re.test(article.excerpt));
                    return {
                        offset: 0,
                        fullData: articles,
                        hasSearched: false,
                        searchData: found.slice(0, NEWS_PER_PAGE),
                        hasMore: found.length > NEWS_PER_PAGE,
                        lastSearchText: '',
                        lastSearchFiltersActive: false,
                    };
                }
            case 'loadMore':
                const newEndpoint = draft.offset + NEWS_PER_PAGE;
                return {
                    offset: draft.offset + NEWS_PER_PAGE,
                    // we're loading more
                    hasSearched: false,
                    searchData: draft.fullData.slice(0, newEndpoint + NEWS_PER_PAGE),
                    fullData: draft.fullData,
                    hasMore: draft.fullData.length > newEndpoint + NEWS_PER_PAGE,
                    lastSearchText: '',
                    lastSearchFiltersActive: false,
                };

            default:
                break;
        }
    };

    const seoOverride: CustomSEO = {
        title: 'News',
    };

    return (
        <Layout seoOverride={seoOverride}>
            <SearchBarProvider
                initialData={{
                    offset: 0,
                    hasSearched: false, //only for server side searching and paging
                    hasMore: articles.length > NEWS_PER_PAGE,
                    searchData: articles.slice(0, NEWS_PER_PAGE) ?? [],
                    fullData: articles ?? [],
                    lastSearchText: '',
                    lastSearchFiltersActive: false,
                }}
                reducer={newsReducer}
            >
                <PageListHeader
                    hasSearchButton={true}
                    isStandaloneHeader={true}
                    className="news-page-list-header"
                    title={'News'}
                />
                <ArticleDisplay />
            </SearchBarProvider>
        </Layout>
    );
}

// statically grab the data from contentful
export const getStaticProps: GetStaticProps = async (ctx) => {
    const contentfulService = container.resolve(ContentfulService);
    const response = await contentfulService.getRecentPosts(20);
    if (response) {
        return {
            props: {
                articles: response.postCollection.items,
            },
            revalidate: 600,
        };
    }

    return {
        props: {
            articles: [],
        },
        revalidate: 600,
    };
};

export default News_Index;
